// src/pages/PurchaseInvoices/PurchaseInvoices.jsx

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot, query, orderBy, doc, getDoc, writeBatch, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import jsPDF from 'jspdf';
import 'jspdf-autotable'; // Changed import to load the plugin properly
import { font } from '../Orders/Roboto-Regular-font';

// --- STYLES ---
const styles = {
    container: { fontFamily: '"Inter", sans-serif' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    button: { padding: '10px 20px', border: 'none', background: '#5c6bc0', color: 'white', borderRadius: '5px', cursor: 'pointer', fontSize: '16px', textDecoration: 'none' },
    box: { background: 'white', padding: '25px', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { background: '#f8f9fa', padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' },
    td: { padding: '12px', borderBottom: '1px solid #eee' },
    trHover: { cursor: 'pointer' },
    backLink: { textDecoration: 'none', color: '#06C167', marginBottom: '15px', display: 'inline-block' },
    grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' },
    infoList: { listStyle: 'none', padding: 0 },
    infoItem: { display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0f0' },
    infoLabel: { color: '#6c757d' },
    infoValue: { fontWeight: '500' },
};

// --- YARDIMCI FONKSİYONLAR ---
const safeString = (value, fallback = '-') => (value === null || typeof value === 'undefined' || value === '') ? fallback : String(value).trim();
const safeNumber = (value, fallback = 0) => (typeof value !== 'number' || isNaN(value)) ? fallback : value;
const sanitizeForPDF = (text) => {
    if (typeof text !== 'string') return text;
    return text.replace(/ı/g, 'i').replace(/İ/g, 'I').replace(/ğ/g, 'g').replace(/Ğ/g, 'G').replace(/ü/g, 'u').replace(/Ü/g, 'U').replace(/ş/g, 's').replace(/Ş/g, 'S').replace(/ö/g, 'o').replace(/Ö/g, 'O').replace(/ç/g, 'c').replace(/Ç/g, 'C');
};

const formatDate = (date) => {
    if (!date) return 'Bilinmiyor';
    if (date.seconds) {
        return new Date(date.seconds * 1000).toLocaleDateString('tr-TR');
    }
    const d = new Date(date);
    if (isNaN(d.getTime())) return 'Hatalı Tarih';
    return d.toLocaleDateString('tr-TR');
};


// --- PDF OLUŞTURMA ---
const generatePurchasePDF = (companyInfo, invoice, products, canViewPrices = true) => {
    try {
        const getProductBarcode = (productId) => products.find(p => p.id === productId)?.barcode || '-';
        const doc = new jsPDF();

        doc.addFileToVFS('Roboto-Regular.ttf', font);
        doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
        doc.setFont('Roboto');

        doc.setFontSize(22);
        doc.setTextColor(44, 62, 80);
        doc.text(sanitizeForPDF(safeString(companyInfo?.companyName, "Firma Adı")), 14, 22);
        doc.setFontSize(18);
        doc.setTextColor(149, 165, 166);
        doc.text(sanitizeForPDF("ALIŞ FATURASI"), 200, 22, { align: 'right' });
        doc.line(14, 28, 200, 28);

        doc.setFontSize(10);
        doc.text(sanitizeForPDF("Tedarikçi Firma Bilgileri"), 14, 40);
        doc.setFontSize(9);
        doc.text(sanitizeForPDF(safeString(invoice.supplierName)), 14, 46);

        doc.setFontSize(10);
        doc.text(sanitizeForPDF("Fatura No:"), 200, 40, { align: 'right' });
        doc.setFontSize(9);
        doc.text((invoice.invoiceNumber || invoice.id).substring(0, 8).toUpperCase(), 200, 46, { align: 'right' });
        doc.setFontSize(10);
        doc.text("Tarih:", 200, 52, { align: 'right' });
        doc.setFontSize(9);
        doc.text(formatDate(invoice.invoiceDate), 200, 58, { align: 'right' });

        const tableColumn = ["Stok Kodu", sanitizeForPDF("Ürün Adı"), "Miktar", "Alis Fiyati", "Toplam"];
        const tableRows = invoice.lineItems.map(item => [
            getProductBarcode(item.productId),
            sanitizeForPDF(item.name),
            item.quantity,
            canViewPrices ? `${safeNumber(item.price).toFixed(2)} TL` : '***',
            canViewPrices ? `${safeNumber(item.total).toFixed(2)} TL` : '***'
        ]);

        doc.autoTable({
            head: [tableColumn],
            body: tableRows,
            startY: 70,
            theme: 'striped',
            headStyles: { fillColor: [44, 62, 80] },
            styles: { font: 'Roboto', fontStyle: 'normal' },
        });

        const finalY = doc.lastAutoTable.finalY;
        doc.setFontSize(12);
        doc.setFont('Roboto', 'bold');
        doc.text(`Genel Toplam:`, 160, finalY + 15, { align: 'right' });
        doc.text(canViewPrices ? `${safeNumber(invoice.total).toFixed(2)} TL` : '***', 200, finalY + 15, { align: 'right' });

        doc.save(`AlisFaturasi-${invoice.id.substring(0, 6)}.pdf`);
    } catch (error) {
        console.error("Alış Faturası PDF'i oluşturulurken hata:", error);
        alert("PDF oluşturulurken bir hata oluştu.");
    }
};

// --- FATURA DETAY BİLEŞENİ ---
function PurchaseInvoiceDetailView({ invoiceId, onBack, canViewPrices }) {
    const { userData } = useAuth();
    const [invoice, setInvoice] = useState(null);
    const [companyInfo, setCompanyInfo] = useState(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [editedItems, setEditedItems] = useState([]);

    useEffect(() => {
        if (!userData?.companyId || !invoiceId) return;

        const companyId = userData.companyId;

        const fetchCompanyInfo = async () => {
            const companyDocRef = doc(db, 'companies', companyId);
            const docSnap = await getDoc(companyDocRef);
            if (docSnap.exists()) setCompanyInfo(docSnap.data());
        };
        fetchCompanyInfo();

        const invoiceRef = doc(db, 'companies', companyId, 'purchaseInvoices', invoiceId);
        const unsubInvoice = onSnapshot(invoiceRef, (docSnap) => {
            if (docSnap.exists()) {
                const invoiceData = { id: docSnap.id, ...docSnap.data() };
                setInvoice(invoiceData);
                setEditedItems(invoiceData.lineItems || []);
            }
        });

        const productsQuery = query(collection(db, 'companies', companyId, 'products'));
        const unsubProducts = onSnapshot(productsQuery, (snapshot) => {
            setProducts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });

        return () => { unsubInvoice(); unsubProducts(); };
    }, [userData, invoiceId]);

    const handleItemChange = (index, field, value) => {
        const newItems = [...editedItems];
        const item = newItems[index];
        item[field] = Number(value);
        item.total = item.quantity * item.price;
        setEditedItems(newItems);
    };

    const handleSaveInvoice = async () => {
        if (!invoice) return;
        const companyId = userData.companyId;
        const batch = writeBatch(db);
        const invoiceRef = doc(db, 'companies', companyId, 'purchaseInvoices', invoice.id);

        const originalItems = invoice.lineItems;
        const newTotal = editedItems.reduce((acc, item) => acc + item.total, 0);
        const balanceDifference = newTotal - invoice.total;

        // Update product quantities based on differences
        for (const newItem of editedItems) {
            const originalItem = originalItems.find(i => i.productId === newItem.productId);
            const quantityDifference = newItem.quantity - (originalItem ? originalItem.quantity : 0);

            if (quantityDifference !== 0) {
                const productRef = doc(db, 'companies', companyId, 'products', newItem.productId);
                const productDoc = await getDoc(productRef);
                if (productDoc.exists()) {
                    const newQuantity = productDoc.data().quantity + quantityDifference;
                    batch.update(productRef, { quantity: newQuantity });
                }
            }
        }

        // Update invoice
        batch.update(invoiceRef, {
            lineItems: editedItems.map(item => ({
                productId: item.productId,
                name: item.name,
                quantity: item.quantity,
                price: item.price,
                total: item.total
            })),
            total: newTotal
        });

        // Update supplier balance
        if (balanceDifference !== 0 && invoice.supplierId) {
            const supplierRef = doc(db, 'companies', companyId, 'suppliers', invoice.supplierId);
            const supplierDoc = await getDoc(supplierRef);
            if (supplierDoc.exists()) {
                const supplier = supplierDoc.data();
                const supplierCurrency = supplier.currency || 'TRY';
                const invoiceCurrency = invoice.currency || 'TRY';
                const exchangeRate = invoice.exchangeRate || 1;

                let balanceDifferenceImpact = balanceDifference;
                if (invoiceCurrency === 'USD' && supplierCurrency === 'TRY') {
                    balanceDifferenceImpact = balanceDifference * exchangeRate;
                } else if (invoiceCurrency === 'TRY' && supplierCurrency === 'USD') {
                    balanceDifferenceImpact = balanceDifference / exchangeRate;
                }

                const currentBalance = supplier.balance || 0;
                batch.update(supplierRef, { balance: currentBalance - balanceDifferenceImpact });
            }
        }

        try {
            await batch.commit();
            setIsEditing(false);
            alert('Fatura başarıyla güncellendi.');
        } catch (error) {
            console.error("Fatura güncellenirken hata:", error);
            alert('Fatura güncellenirken hata oluştu.');
        }
    };

    const handleDeleteInvoice = async () => {
        if (!invoice) return;
        if (!window.confirm("Bu alış faturasını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz ve stok miktarları düşürülecektir.")) {
            return;
        }

        const companyId = userData.companyId;
        const batch = writeBatch(db);

        // Delete invoice
        const invoiceRef = doc(db, 'companies', companyId, 'purchaseInvoices', invoice.id);
        batch.delete(invoiceRef);

        // Revert product quantities
        for (const item of invoice.lineItems) {
            const productRef = doc(db, 'companies', companyId, 'products', item.productId);
            const productDoc = await getDoc(productRef);
            if (productDoc.exists()) {
                const currentQuantity = productDoc.data().quantity || 0;
                const newQuantity = currentQuantity - item.quantity;
                batch.update(productRef, { quantity: newQuantity >= 0 ? newQuantity : 0 });
            }
        }

        // Update supplier balance (add back the invoice total)
        if (invoice.supplierId) {
            const supplierRef = doc(db, 'companies', companyId, 'suppliers', invoice.supplierId);
            const supplierDoc = await getDoc(supplierRef);
            if (supplierDoc.exists()) {
                const supplier = supplierDoc.data();
                const supplierCurrency = supplier.currency || 'TRY';
                const invoiceCurrency = invoice.currency || 'TRY';
                const exchangeRate = invoice.exchangeRate || 1;

                let balanceRevertImpact = invoice.total;
                if (invoiceCurrency === 'USD' && supplierCurrency === 'TRY') {
                    balanceRevertImpact = invoice.total * exchangeRate;
                } else if (invoiceCurrency === 'TRY' && supplierCurrency === 'USD') {
                    balanceRevertImpact = invoice.total / exchangeRate;
                }

                const currentBalance = supplier.balance || 0;
                batch.update(supplierRef, { balance: currentBalance - balanceRevertImpact });
            }
        }

        try {
            await batch.commit();
            onBack();
            alert('Fatura başarıyla silindi.');
        } catch (error) {
            console.error("Fatura silinirken hata:", error);
            alert('Fatura silinirken hata oluştu.');
        }
    };

    if (loading) return <p>Yükleniyor...</p>;
    if (!invoice) return <h2>Fatura Bulunamadı</h2>;

    const currencySymbol = invoice.currency === 'USD' ? '$' : '₺';
    const editedTotal = editedItems.reduce((acc, item) => acc + item.total, 0);

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)'
        }} onClick={onBack}>
            <div style={{
                background: 'white',
                borderRadius: '8px',
                width: '90%',
                maxWidth: '900px',
                maxHeight: '90vh',
                overflowY: 'auto',
                position: 'relative',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
            }} onClick={e => e.stopPropagation()}>
            <div style={{
                padding: '24px 30px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: '#f8fafc'
            }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '800', color: '#1e293b' }}>Alış Faturası Detayı</h2>
                    <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '14px' }}>#{invoice.invoiceNumber || invoice.id.substring(0, 8).toUpperCase()}</p>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                    {isEditing ? (
                        <>
                            <button onClick={() => { setIsEditing(false); setEditedItems(invoice.lineItems); }} style={{ ...styles.button, background: '#64748b' }}>İptal</button>
                            <button onClick={handleSaveInvoice} style={{ ...styles.button, background: '#10b981' }}>Kaydet</button>
                        </>
                    ) : (
                        <>
                            <button onClick={() => setIsEditing(true)} style={{ ...styles.button, background: '#06C167' }}>Düzenle</button>
                            <button onClick={handleDeleteInvoice} style={{ ...styles.button, background: '#ef4444' }}>Sil</button>
                            <button onClick={() => generatePurchasePDF(companyInfo, invoice, products, canViewPrices)} style={{ ...styles.button, background: '#10b981' }} disabled={!companyInfo || !products.length}>PDF</button>
                        </>
                    )}
                    <button 
                        onClick={onBack} 
                        style={{
                            background: '#f1f5f9',
                            border: 'none',
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            fontSize: '20px',
                            color: '#64748b',
                            marginLeft: '8px'
                        }}
                    >
                        &times;
                    </button>
                </div>
            </div>

            <div style={{ padding: '30px', flex: 1, overflowY: 'auto' }}>
                <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr', 
                    gap: '24px', 
                    marginBottom: '32px',
                    background: '#f8fafc',
                    padding: '24px',
                    borderRadius: '20px',
                    border: '1px solid #e2e8f0'
                }}>
                    <div>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '8px' }}>Tedarikçi</div>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>{invoice.supplierName}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '8px' }}>Tarih / Tutar</div>
                        <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: '600' }}>{new Date(invoice.invoiceDate.seconds * 1000).toLocaleDateString('tr-TR')}</div>
                        <div style={{ fontSize: '18px', fontWeight: '800', color: '#06C167', marginTop: '4px' }}>
                            {canViewPrices ? `${(isEditing ? editedTotal : invoice.total).toFixed(2)} ${currencySymbol}` : '***'}
                        </div>
                    </div>
                </div>

                <div style={{ border: '1px solid #e2e8f0', borderRadius: '16px', overflow: 'hidden', background: 'white' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead style={{ background: '#f8fafc' }}>
                            <tr>
                                <th style={{ padding: '16px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#475569', fontSize: '13px' }}>Ürün Adı</th>
                                <th style={{ padding: '16px', textAlign: 'center', borderBottom: '1px solid #e2e8f0', color: '#475569', fontSize: '13px' }}>Miktar</th>
                                <th style={{ padding: '16px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#475569', fontSize: '13px' }}>Alış Fiyatı</th>
                                <th style={{ padding: '16px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#475569', fontSize: '13px' }}>Toplam</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(isEditing ? editedItems : invoice.lineItems).map((item, index) => (
                                <tr key={index}>
                                    <td style={{ padding: '16px', borderBottom: '1px solid #f1f5f9', color: '#1e293b', fontWeight: '500' }}>{item.name}</td>
                                    <td style={{ padding: '16px', borderBottom: '1px solid #f1f5f9', textAlign: 'center' }}>
                                        {isEditing ? (
                                            <input
                                                type="number"
                                                value={item.quantity}
                                                onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                                style={{ width: '70px', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '8px', textAlign: 'center' }}
                                            />
                                        ) : (
                                            <span style={{ color: '#1e293b' }}>{item.quantity}</span>
                                        )}
                                    </td>
                                    <td style={{ padding: '16px', borderBottom: '1px solid #f1f5f9', textAlign: 'right' }}>
                                        {isEditing ? (
                                            <input
                                                type="number"
                                                value={item.price}
                                                onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                                                style={{ width: '100px', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '8px', textAlign: 'right' }}
                                            />
                                        ) : (
                                            <span style={{ color: '#1e293b' }}>{canViewPrices ? `${item.price.toFixed(2)} ${currencySymbol}` : '***'}</span>
                                        )}
                                    </td>
                                    <td style={{ padding: '16px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', color: '#1e293b', fontWeight: '700' }}>
                                        {canViewPrices ? `${item.total.toFixed(2)} ${currencySymbol}` : '***'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
);
}

// --- ANA ALIŞ FATURALARI SAYFASI ---
function PurchaseInvoices() {
    const { userData, canViewPrices } = useAuth();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

    // Modern Tasarım İçin Yeni Stiller
    const modernStyles = {
        ...styles,
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '32px'
        },
        title: {
            fontSize: '32px',
            fontWeight: '700',
            color: '#0f172a',
            margin: 0,
            letterSpacing: '-0.5px'
        },
        subtitle: {
            fontSize: '16px',
            color: '#64748b',
            marginTop: '4px'
        },
        createButton: {
            padding: '12px 24px',
            border: 'none',
            background: 'linear-gradient(135deg, #06C167, #8b5cf6)',
            color: 'white',
            borderRadius: '12px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
            transition: 'all 0.2s ease-in-out',
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
        },
        tableContainer: {
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.05)',
            border: '1px solid #e2e8f0',
            overflow: 'hidden'
        },
        table: {
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: 0,
        },
        th: {
            background: '#f8fafc',
            padding: '16px 24px',
            textAlign: 'left',
            color: '#64748b',
            fontWeight: '600',
            fontSize: '13px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            borderBottom: '1px solid #e2e8f0'
        },
        td: {
            padding: '16px 24px',
            borderBottom: '1px solid #f1f5f9',
            color: '#334155',
            fontSize: '14px',
            fontWeight: '500'
        },
        tr: {
            transition: 'all 0.2s ease',
            cursor: 'pointer'
        },
        currencyBadge: {
            display: 'inline-flex',
            alignItems: 'center',
            padding: '4px 10px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '600',
            background: '#f1f5f9',
            color: '#475569',
            marginLeft: '8px'
        },
        invoiceNumber: {
            fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Fira Mono", "Droid Sans Mono", "Source Code Pro", monospace',
            color: '#06C167',
            fontWeight: '600',
            background: 'rgba(99, 102, 241, 0.1)',
            padding: '4px 8px',
            borderRadius: '6px',
            fontSize: '13px'
        },
        emptyState: {
            padding: '60px',
            textAlign: 'center',
            color: '#94a3b8'
        }
    };

    useEffect(() => {
        if (!userData?.companyId) {
            setLoading(false);
            return;
        }
        const companyId = userData.companyId;
        const q = query(collection(db, 'companies', companyId, 'purchaseInvoices'), orderBy('invoiceDate', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setInvoices(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });
        return () => unsubscribe();
    }, [userData]);

    return (
        <div style={styles.container}>
            <div style={modernStyles.header}>
                <div>
                    <h1 style={modernStyles.title}>Alış Faturaları</h1>
                    <p style={modernStyles.subtitle}>Tedarikçi ödemelerinizi ve faturalarınızı yönetin</p>
                </div>
                <Link to="/hammadde-satin-alma/yeni" style={modernStyles.createButton}>
                    <span>+</span> Yeni Fatura Oluştur
                </Link>
            </div>

            <div style={modernStyles.tableContainer}>
                {loading ? (
                    <div style={{ padding: '40px', textAlign: 'center' }}>Yükleniyor...</div>
                ) : (
                    <table style={modernStyles.table}>
                        <thead>
                            <tr>
                                <th style={modernStyles.th}>Fatura No</th>
                                <th style={modernStyles.th}>Tedarikçi</th>
                                <th style={modernStyles.th}>Tarih</th>
                                <th style={modernStyles.th}>Tutar</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.length > 0 ? invoices.map(invoice => {
                                // Para birimi sembolü ve formatlama
                                const currency = invoice.currency || 'TRY';
                                const currencySymbol = currency === 'USD' ? '$' : (currency === 'EUR' ? '€' : (currency === 'GBP' ? '£' : '₺'));

                                return (
                                    <tr
                                        key={invoice.id}
                                        style={modernStyles.tr}
                                        onClick={() => setSelectedInvoiceId(invoice.id)}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <td style={modernStyles.td}>
                                            <span style={modernStyles.invoiceNumber}>
                                                {invoice.invoiceNumber || invoice.id.substring(0, 8).toUpperCase()}
                                            </span>
                                        </td>
                                        <td style={modernStyles.td}>
                                            <div style={{ fontWeight: 600, color: '#0f172a' }}>{invoice.supplierName}</div>
                                        </td>
                                        <td style={modernStyles.td}>
                                            {formatDate(invoice.invoiceDate)}
                                        </td>
                                        <td style={modernStyles.td}>
                                            {canViewPrices ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                                    {currency !== 'TRY' && (
                                                        <span style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>
                                                            {invoice.total.toFixed(2)} {currencySymbol}
                                                        </span>
                                                    )}
                                                    <span style={{
                                                        fontSize: currency !== 'TRY' ? '12px' : '15px',
                                                        fontWeight: currency !== 'TRY' ? '500' : '700',
                                                        color: currency !== 'TRY' ? '#64748b' : '#0f172a'
                                                    }}>
                                                        {currency === 'TRY' ? `${invoice.total.toFixed(2)} ₺` : `${(invoice.totalTRY || (invoice.total * (invoice.exchangeRate || 1))).toFixed(2)} ₺`}
                                                    </span>
                                                </div>
                                            ) : '***'}
                                        </td>
                                    </tr>
                                );
                            }) : (
                                <tr>
                                    <td colSpan="4" style={modernStyles.emptyState}>
                                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🧾</div>
                                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#334155' }}>Henüz alış faturası bulunmuyor</div>
                                        <div style={{ color: '#64748b', marginTop: '8px' }}>Yeni bir fatura oluşturarak başlayabilirsiniz.</div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {selectedInvoiceId && (
                <PurchaseInvoiceDetailView 
                    invoiceId={selectedInvoiceId} 
                    onBack={() => setSelectedInvoiceId(null)} 
                    canViewPrices={canViewPrices} 
                />
            )}
        </div>
    );
}

export default PurchaseInvoices;
